import { StaticImage } from "gatsby-plugin-image"

// Assuming these imports are still needed for the component structure
import LinkedInIcon from "../../images/case-study/ugc/linkedin.svg"
import icon1 from "../../images/svg/1.svg"
import icon2 from "../../images/svg/2.svg"
import icon3 from "../../images/svg/3.svg"
import icon4 from "../../images/svg/4.svg"
import { info } from "console"
import { title } from "process"
import { ImagePositions } from "./CaseStudy"

const gitexGlobal2025Data = {
  color: "#ffe5e5",
  heroTitle: `Attendee-led marketing that contributed to GITEX GLOBAL’s 200K+ attendance`,
  heroImage: (
    <StaticImage
      src="../../images/case-study/gitex-global-2025/hero-inside.png"
      alt="gitex-global-dubai-tech-conference-photo-ai-hero"
    />
  ),
  metrics: {
    content: `<a href="https://www.gitex.com/" target="_blank">GITEX GLOBAL</a> 2025, held from <b>October 6th to 10th in Dubai</b> attracted over 200,000 attendees from around the world — making it the largest tech show on the planet. This record-breaking 45th edition saw participation from over 180 countries, featuring more than 6,800 exhibitors, 2,000 startups, and 1,200 investors, with the event taking place across the Dubai World Trade Centre and Dubai Harbour.<br/><br/>
With that scale comes immense pressure to deliver. “The biggest thing for us year on year is to ensure that our exhibitors and visitors are getting the results they need after making the effort to travel all the way from different parts of the world to Dubai,” stated <b>Shreya Shyam Sunder</b>, Marketing Executive at DWTC. The stakes were high. And the approach in 2025 would be different from anything they'd done before.
`,
    values: [
      {
        label: "Activated Attendees",
        value: "32K+",
      },
      {
        label: "Referrals Per Advocate",
        value: "3.7",
      },
      {
        label: "Potential Reach",
        value: "5.97M+",
      },
      {
        label: "Ad Spend Saved",
        value: "$160k+",
      },
    ],
  },
  images: [
    {
      img: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/banner.png"
          alt="gitex-global-dubai-tech-conference-photo-ai-primary-banner"
        />
      ),
    },
  ],
  gallery: (
    <StaticImage
      src="../../images/case-study/gitex-global-2025/gallery.png"
      alt="gitex-global-dubai-tech-conference-photo-ai-gallery"
    />
  ),
  caseStudyData: [
    {
      title: "Leaving Outdated Tools Behind",
      info1: `In 2024, the GITEX team relied on two separate solutions — a dedicated pre-event engagement tool, and Premagic, used exclusively for <a href="https://premagic.com/case-study/gitex-global/" target="_blank">during- and post-event engagement</a>.<br/><br/>The results delivered by Premagic, the flexibility, the quality of execution, all of it stood out sharply against the dedicated pre-event tool they were using alongside it.<br/><br/>So they asked a natural question: if Premagic could deliver this during and after the event, what could it do before it?<br/><br/>The answer changed everything. Once they saw the depth of Premagic's pre-event capabilities, the decision was straightforward — why manage multiple fragmented tools when one platform could own the entire event lifecycle? True impact, they realised, wouldn't come from using Premagic in isolation. It would come from running it end-to-end, as a unified engine across every stage of GITEX.<br/><br/>"Over the years, we've steadily ramped up how we work with Premagic. It started with AI photo distribution.” stated Shreya. “This year, we've taken it to a full-fledged partnership by integrating social advocacy and engagement across the event."<br/><br/>That realisation led to a decisive move. The GITEX team abandoned their existing pre-event engagement tool entirely. Three reasons drove that decision:<br/><br/><ul style='list-style-type: disc; padding-left: 20px;'>
  <li style='list-style-type: disc; display: list-item;'>
    <strong>Not innovative:</strong> the tool had stagnated with no meaningful product evolution
  </li>
  <br />
  <li style='list-style-type: disc; display: list-item;'>
    <strong>Not flexible:</strong> it couldn't adapt to the scale or complexity of GITEX's needs
  </li>
  <br />
  <li style='list-style-type: disc; display: list-item;'>
    <strong>Limited scope:</strong> it only handled pre-event engagement, with no ability to deliver continuous engagement or carry momentum into the next edition
  </li>
</ul><br/><br/>For 2025, the goal was clear: move beyond isolated activations and integrate Premagic across the <b>entire event lifecycle — pre-event, during the event, and post-event</b>.
`,
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/first.png"
          alt="GITEX Global 2025 introduction"
        />
      ),
      imagePosition: "top",
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/gitex-global-2025/shreya.png"
            alt="gitex-global-dubai-tech-conference-photo"
            className="!w-full"
            width={800}
          />
        ),
        message: `<i>"We switched to Premagic because our previous tools were too outdated to deliver the results we needed. Premagic is creative, modern, and incredibly user-friendly."</i>`,
        name: "Shreya Shyam Sunder",
        info: "Marketing Executive <br/> DWTC",
      },
    },
    {
      title: "Kicking Off the Campaign with Advocacy at Scale",
      info1: `The campaign kicked off with Premagic's branded advocacy poster module — enabling attendees, speakers, and exhibitors to share personalized event banners across their social networks before the event even began.`,
      info2: `The DWTC team designed their own poster to launch the campaign. However, after monitoring early results, it became clear that the design wasn't driving the conversions expected. The Premagic team stepped in.<br/><br/>Our design team analyzed performance, applied best practices, and delivered a new, optimized design. Conversions increased by <b>5%</b>, and campaign momentum picked up significantly.<br/><br/>This moment crystallized something important for both teams: <b>enterprise clients like GITEX don't just need a SaaS platform — they need a dedicated event marketing team that can execute alongside them</b>. And as for Premagic, our speciality is that <b>we work as an extension of your team.</b><br/><br/>By the time the campaign had built steam, the numbers were already turning heads. Compared to the previous year, <b>Premagic drove 39.6% more advocates who created 24.7% more posts, dramatically increasing the event's organic reach even before the event had started yet.</b><br/><br/>By the end of the campaign: <b>18,166 direct advocacy posts and 4,560 indirect posts had been generated creating a potential reach of 5.97M+</b>.
`,
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/gitex-global-2025/vinod.png"
            alt="gitex-global-dubai-tech-conference-photo"
            className="!w-full"
            width={800}
          />
        ),

        message: `<i>"Premagic integrated perfectly into the full event cycle: pre-event promotion, onsite engagement, and post-event analytics. Few tools can truly support the entire journey at scale, but Premagic delivered. The clarity it brought to attendee engagement and content distribution was remarkable."</i>`,
        name: "Vinod Nalawade",
        info: "Global Events & Operations <br/> Specialist",
      },
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/posters-new.png"
          alt="GITEX Global 2025 introduction"
        />
      ),
      imagePosition: "top",
    },
    {
      title: "Expanding Reach: From Email to WhatsApp",
      info1: `Email campaigns alone weren't enough to reach every attendee segment. So the team expanded to WhatsApp campaigns — and the impact was immediate. Engagement jumped significantly, advocacy activations increased, and weekend registrations saw a noticeable uptick. Speaker and exhibitor sharing also picked up meaningfully, with WhatsApp proving to be a far more direct and effective channel for those audiences in particular.<br/><br/>Before the event had even opened its doors: <b>3.7 referrals generated per advocate. $160,000+ in ad spend saved.</b>`,
      info2: `What made this work wasn't just the channel expansion — it was the proactive support behind it. Without waiting to be asked, the Premagic team regularly reached out to DWTC with progress updates, suggestions, and next steps. A dedicated WhatsApp group was created with the DWTC team to provide real-time support at every stage. The DWTC team noticed and appreciated it deeply.`,
      video: `https://vimeo.com/1181498103?fl=pl&fe=sh`,
      imagePosition: "center",
      // CTA: {
      //   text: "See Premagic in Action",
      //   link: "https://premagic.com/request-a-demo/?utm_source=casestudy&utm_medium=website&utm_id=opentext",
      //   position: "bottom",
      // },
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/one-platform.png"
          alt="gitex-global-dubai-tech-conference-photo-ai-secondary-banner"
        />
      ),
    },
    {
      title: `Crystal Clear and Transparent Data`,
      info1: `The Premagic dashboard made every bit of that progress visible in real time.`,

      imagePosition: "center",
      info2: `Unlike the previous tech partner, the Premagic dashboard offered a level of transparency and granularity the team hadn't experienced before — number of shares, individual advocate activity, referrals generated per share, and posters broken down by attendee type. <b>This data visibility was a genuine differentiator.</b>`,
    },
    {
      title: "From Pre-Event Buzz to On-Site Energy",
      info1: `By the time GITEX 2025 opened its doors, the pre-event buzz had already done its job — thousands of attendees engaged, meaningful reach built, and organic advocacy well underway.<br/><br/>As attendees arrived at the venue, they encountered Premagic's QR code prominently placed as part of the official event collateral. Having witnessed the value of the QR-based photo experience at the previous edition, the DWTC team proactively had the QR standee printed and integrated into their event setup — no prompting needed.`,
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/scanning.jpeg"
          alt="GITEX Global 2025 AI avatars"
        />
      ),
      imagePosition: "center",
    },
    {
      title: "On-Ground Execution and Full Event Coverage",
      info1: `A dedicated Premagic team was present on the ground throughout the event. Exhibitors saw results in real time — attendees were sharing their experience across social media without any friction. Premagic's own photographers were deployed across the venue to ensure complete event coverage, capturing the energy of GITEX between every engagement touchpoint.`,
      info2: `Attendees scanned. <b>20,000+ photos were distributed instantly on-site</b>. And in the time it took for those photos to arrive on their phones, many discovered the <b>AI Avatar</b> feature — personalizing their experience and sharing it organically across social media. Many created multiple versions of their avatars, experimenting with different looks and sharing them instantly on social media.`,
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/on-ground.png"
          alt="GITEX Global 2025 AI avatars"
        />
      ),
      imagePosition: "center",
    },
    {
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/avatars.png"
          alt="GITEX Global 2025 AI avatars"
        />
      ),
      imagePosition: "center",
    },
    {
      title: "Continuous Engagement from beginning to end",
      info1: `The contrast with 2024 was stark.`,
      info2: `<b>2024:</b> Engagement was fragmented and fell short of what the team had hoped for. Pre-event activity existed in isolation. It stopped where it started, never building into anything bigger, and ultimately didn't deliver the results GITEX needed.</br></br> <b>2025:</b> A seamless engagement arc ran the full length of the event, from pre-event advocacy and buzz, through live photo delivery and AI avatar moments on-site, to post-event sharing and referral activity that kept the conversation going long after the doors closed.`,
      image: (
        <StaticImage
          src="../../images/case-study/gitex-global-2025/posts.png"
          alt="GITEX Global 2025 AI avatars"
        />
      ),
      imagePosition: "center",
    },

    {
      title: "Ready to Move Beyond a Pure SaaS Tool?",
      info1: `If your event engagement is stuck in pre-event or post-event silos because of the limitations of the tools you're using,  it's time to think differently.<br/><br/>Here is a video to give you the whole picture of why exactly Gitex Global made the switch to Premagic. `,
      video: `https://player.vimeo.com/video/1159064520?h=b8432f1c3b`,
      imagePosition: "center",
      info2: `<b>One tool. One team. The entire event lifecycle — covered.</b>`,
    },
  ],

  // successMetrics: {
  //   title: "Key Success Metrics",
  //   description:
  //     "The platform effectively mobilized the <b>172,566 participants</b> to become active promoters of the event.",
  //   gridValues: [
  //     { id: 1, label: "Poster Shares", value: "16,198" },
  //     { id: 2, label: "Referrals Generated", value: "47,876" },
  //     { id: 3, label: "Ad Spend Saved", value: "$212,262" },
  //     { id: 4, label: "Photo Views", value: "160,531" },
  //     { id: 5, label: "Photo Downloads", value: "2,218" },
  //     { id: 6, label: "Share Rate Increase", value: "94.2%" },
  //     { id: 7, label: "AI Avatars Created", value: "246" },
  //     { id: 8, label: "Ticket Sales Impact", value: "19,907" },
  //   ],
  // },
  isCTAActive: true,
}

export default gitexGlobal2025Data
